import React, { useState, useEffect, useRef } from 'react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

import { Link } from 'react-router-dom';
import { cloneDeep } from 'lodash';

import { api } from '../../../services/api';

import BreadCrumbArrow from '../../../images/icons/breadcrumb-arrow.svg';
import AccountService from './account-service';
import BasicDropdown from '../../base/dropdowns/basic-dropdown';
import Flag from '../../base/flag/flag';
import UserModal from './account-user-modal';
import NetworkLists from './network-lists';
import AccountDetails from './form-account-details';
import PublisherAuctionSetup from './form-publisher-auction-setup';
import MerchantAuctionSetup from './form-merchant-auction-setup';
import FeedConfigurationAndTracking from './form-feed-configuration-tracking';
import MandatoryFormItem from './form-mandatory-item';
import Icon from '../../ui/icon';

import './styles/manage-account.scss';
import SnackBar from './snackbar';
import UnlinkModal from './unlink-modal';

const roleOptions = [
  { label: 'Primary', value: true },
  { label: 'Secondary', value: false },
];

const auctionTypes = [
  { label: 'Click-based', value: false },
  { label: 'Impression-based', value: true },
];

const pubTypes = [
  { label: 'Publisher', value: 'publisher' },
  { label: 'Creator', value: 'creator' },
];

const MERCHANT = 'merchant';
const PUBLISHER = 'publisher';

const pricingModels = [
  { label: 'CPC', value: 'cpc' },
  { label: 'EPP', value: 'epp' },
];

const ManageAccount = ({ match, history, flags }) => {
  const editing = history.location.pathname.includes('edit');
  const { accountType } = match.params;
  const { accountId } = match.params;
  const overviewPath = '/v2/admin/accounts/overview/';
  const otherOrgType = accountType === PUBLISHER ? MERCHANT : PUBLISHER;

  const accountDetailsRef = useRef(null);
  const usersRef = useRef(null);
  const feedConfigRef = useRef(null);
  const merchantAuctionRef = useRef(null);
  const pubAuctionRef = useRef(null);
  const networkRef = useRef(null);

  const auctionRef = accountType === PUBLISHER ? pubAuctionRef : merchantAuctionRef;
  const sections = [
    { name: 'Account Details', ref: accountDetailsRef },
    { name: 'Users', ref: usersRef },
    ...(accountType !== PUBLISHER ? [{ name: 'Feed Configuration', ref: feedConfigRef }] : []),
    { name: 'Auction Setup', ref: auctionRef },
    { name: `In-Network ${otherOrgType}s`, ref: networkRef },
  ];

  const uploadButton = useRef(null);

  // Non-Form field state
  const [didMount, setDidMount] = useState(false);
  const [account, setAccount] = useState({});
  const [modal, setModal] = useState(null);
  const [fieldErrors, setFieldErrors] = useState([]);
  const [snackbarTxt, setSnackbarTxt] = useState('');
  const [modalData, setModalData] = useState({});
  const [editableSlug, setEditableSlug] = useState(false);

  // Account detail fields
  const [pubType, setPubType] = useState(pubTypes[0]);
  const [fullName, setFullName] = useState('');
  const [shortName, setShortName] = useState('');
  const [categories, setCategories] = useState([]);
  const [url, setUrl] = useState('');
  const [slug, setSlug] = useState('');
  const [selectedFile, setFile] = useState(null);
  const [pilot, setPilot] = useState(false);
  const [showInDashboard, setShowInDashboard] = useState(false);
  const [accountLogo, setAccountLogo] = useState(null);
  const [isActive, setIsActive] = useState(null);

  // In/out network
  const [inNetworkOrgs, setInNetworkOrgs] = useState([]);
  const [outOfNetworkOrgs, setOutOfNetworkOrgs] = useState([]);

  // Users
  const [users, setUsers] = useState([]);
  const [billingUserOptions, setBillingUserOptions] = useState([]);
  const [selectedBillingUser, setSelectedBillingUser] = useState(null);
  const [savedBillingUserId, setSavedBillingUserId] = useState(null);
  const [invoiceRecipient, setInvoiceRecipient] = useState('');

  // Publisher auction setup
  const [reserveCpc, setReserveCpc] = useState('');
  const [cogs, setCogs] = useState('.6');
  const [auctionType, setAuctionType] = useState(auctionTypes[0]);
  const [amazonId, setAmazonId] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [enableTheTake, setEnableTheTake] = useState(false);
  const [enableLinkmate, setEnableLinkmate] = useState(false);
  const [enableAmp, setEnableAmp] = useState(false);
  const [recordExternalClicks, setRecordExternalClicks] = useState(false);
  const [jstagDisabled, setJstagDisabled] = useState(false);
  const [validDomains, setValidDomains] = useState(['']);

  // Merchant auction setup
  const [currentPricingModel, setCurrentPricingModel] = useState({});
  const [maxCPC, setMaxCPC] = useState('');
  const [roiGoal, setRoiGoal] = useState('');
  const [shareGoal, setShareGoal] = useState('');
  const [goalCPC, setGoalCPC] = useState('');
  const [commissionRate, setCommissionRate] = useState('');
  const [pacing, setPacing] = useState(null);

  // Affiliate settings
  const affiliateTypes = [
    { label: 'Default (Original URL)', value: 'Default' },
    { label: 'Impact Radius', value: 'ImpactRadius' },
    { label: 'Impact Radius Subids', value: 'ImpactRadiusSubIds' },
    ...(accountType === MERCHANT
      ? [{ label: 'Amazon', value: 'Amazon', inputLabel: 'Amazon Tracking ID' }]
      : []),
    { label: 'Linkshare', value: 'Linkshare' },
    { label: 'Original Url', value: 'Original' },
    { label: 'Skimlinks', value: 'Skimlinks' },
    { label: 'Viglink', value: 'Viglink' },
    { label: 'Ninth Decimal', value: 'NinthDecimal' },
    { label: 'Connexity', value: 'Connexity' },
    { label: 'PepperJam', value: 'PepperJam' },
    { label: 'YesStyle', value: 'YesStyle' },
    { label: 'PMG', value: 'PMG' },
  ];
  const [amazonConfig, setAmazonConfig] = useState('');
  const [impactRadiusUrl, setImpactRadiusUrl] = useState('');
  const [linkShareMemberId, setLinkShareMemberId] = useState('');
  const [pmgUrl, setPmgUrl] = useState('');
  const [skimLinkId, setSkimLinkId] = useState('');
  const [vigilinkKey, setVigilinkKey] = useState('');
  const [defaultAffiliate, setDefaultAffiliate] = useState(affiliateTypes[0]);
  const [linkmateAffiliate, setLinkmateAffiliate] = useState(affiliateTypes[0]);
  const [notes, setNotes] = useState('');

  // Feed configuration and tracking
  const [lockFtpName, setLockFtpName] = useState(false);
  const [ftpName, setFtpName] = useState('');
  const [ftpPassword, setFtpPassword] = useState('');
  const [expectedFrequency, setExpectedFrequency] = useState('');
  const [impressionTracker, setImpressionTracker] = useState('');
  const [clickTracker, setClickTracker] = useState('');
  const [logging, setLogging] = useState('');
  const [feedEnabled, setFeedEnabled] = useState(null);
  const [utmParams, setUtmParams] = useState([['', '']]);
  const [merchBillingOption, setMerchBillingOption] = useState(true);
  const [campaignInvoicing, setCampaignInvoicing] = useState(false);

  useEffect(() => {
    setDidMount(true);
  }, []);

  useEffect(() => {
    getAccount();
    getNetworkOrgs();
  }, [history.location.pathname]);

  useEffect(() => {
    if (savedBillingUserId) getAccountUsers();
  }, [savedBillingUserId]);

  useEffect(() => {
    if (slug && !editableSlug) saveSlug();
  }, [slug]);

  useEffect(() => {
    getBillingUserOptions();
  }, [users]);

  useEffect(() => {
    if (modal) setSnackbarTxt('');
  }, [modal]);

  useEffect(() => {
    if (didMount && selectedFile) {
      uploadImage();
    }
  }, [selectedFile]);

  function getAffiliateConfigKey(config, type, key) {
    return config.hasOwnProperty(type) && config[type][key] ? config[type][key] : '';
  }

  // API requests
  function getAccount() {
    AccountService.getAccount(accountType, accountId)
      .then(res => {
        const account = res.data.data[0];
        const { domains } = account.widget_options;
        const config = account.affiliate_config || affiliateTypes[0];
        setAccount(account);
        setAccountLogo(account.logo_url);
        setPubType(pubTypes[account.publisher_type - 1]);
        setFullName(account.full_name);
        setShortName(account.short_name);
        setCategories(account.categories || []);
        setUrl(account.url || '');
        setPilot(account.is_pilot);
        setShowInDashboard(account.show_in_dashboard);
        setReserveCpc(account.reserve_cpc);
        setCogs(account.cogs || '');
        setAuctionType(auctionTypes.find(type => type.value === account.has_jstag));
        setAmazonId(account.amazon_tracking_id || '');
        setTermsAccepted(account.terms_accepted);
        setEnableTheTake(!!account.widget_options.enable_external_script);
        setEnableLinkmate(account.widget_options.linkmate);
        setEnableAmp(account.widget_options.amp);
        setRecordExternalClicks(account.widget_options.record_external_clicks || false);
        setJstagDisabled(account.widget_options.jstag_disabled || false);
        setValidDomains(domains && domains.length ? domains : ['']);

        // Affiliate settings
        setDefaultAffiliate(
          account.affiliate_config
            ? affiliateTypes.find(type => type.value === account.affiliate_config.default) ||
                affiliateTypes[0]
            : affiliateTypes[0],
        );
        setLinkmateAffiliate(
          account.affiliate_config
            ? affiliateTypes.find(type => type.value === account.affiliate_config.linkmate) ||
                affiliateTypes[0]
            : affiliateTypes[0],
        );
        setAmazonConfig(getAffiliateConfigKey(config, 'amazon_config', 'id'));
        setImpactRadiusUrl(getAffiliateConfigKey(config, 'impact_radius_config', 'tracking_url'));
        setSkimLinkId(getAffiliateConfigKey(config, 'skimlinks_config', 'id'));
        setPmgUrl(getAffiliateConfigKey(config, 'pmg_config', 'tracking_url'));
        setVigilinkKey(getAffiliateConfigKey(config, 'viglink_config', 'key'));
        setLinkShareMemberId(getAffiliateConfigKey(config, 'linkshare_config', 'member_id'));
        setNotes(account.notes || '');

        // Merchant account fields
        setIsActive(account.is_active);
        setCurrentPricingModel(
          pricingModels.find(model => model.label === account.pricing_model) || pricingModels[0],
        );
        setFtpName(account.ftp_account_name || '');
        setLockFtpName(!!account.ftp_account_name);
        setFtpPassword(account.ftp_account_password || '');
        setInvoiceRecipient(account.invoice_email || '');
        setMerchBillingOption(
          typeof account.invoicing_enabled === 'boolean' ? account.invoicing_enabled : true,
        );
        setCampaignInvoicing(!!account.campaign_invoicing_enabled);
        setMaxCPC(account.max_cpc || '');
        setShareGoal(account.share_goal || '');
        setRoiGoal(account.roi_goal || '');
        setGoalCPC(account.cpc_goal || '');
        setCommissionRate(account.commission_rate || '');
        setUtmParams(
          account.utm_params && account.utm_params.length ? account.utm_params : utmParams,
        );
        setClickTracker(account.click_tracker || '');
        setFeedEnabled(account.is_feed_importer_enabled);
        setExpectedFrequency(account.expected_import_frequency);
        setPacing(account.is_pacing_enabled);
        setImpressionTracker(account.impression_tracker || '');
        setLogging(account.widget_options.enable_verbose_logging);
        setSavedBillingUserId(account.billing_user_id || -1);

        if (account.slug) {
          setSlug(account.slug);
        } else {
          setSlug(getSlug(account.short_name));
        }
      })
      .catch(e => showError(`Getting account failed, ${e && e.message}`));
  }

  function saveSlug() {
    const params = { slug };
    return AccountService.saveAccountSlug(params, accountType, accountId).catch(e => {
      if (Array.isArray(e)) {
        showError(e[0]);
        setFieldErrors(e[1]);
      } else if (e.code !== 5020) {
        // Slug still saves if 5020 error
        showError(e);
        if (e.code === 4400) {
          // Another org has the slug
          setEditableSlug(true);
        }
      }
    });
  }

  function getSlug(shortName) {
    // Remove spaces and special characters
    return shortName
      .toLowerCase()
      .replace(/ /g, '')
      .replace(/[^A-Za-z0-9 ]/g, '');
  }

  function getParams() {
    const baseParams = {
      full_name: fullName,
      short_name: shortName,
      url,
      slug,
      logo_url: accountLogo,
      affiliate_config: {
        default: defaultAffiliate.value,
        linkmate: linkmateAffiliate.value,
      },
      is_pilot: pilot,
      show_in_dashboard: showInDashboard,
      notes: notes || null,
      ...(flags.accountCategorization ? { categories: categories.join(',') } : {}),
    };

    // Merchant optional fields
    if (ftpName) baseParams.ftp_account_name = ftpName;
    if (ftpPassword) baseParams.ftp_account_password = ftpPassword;
    if (goalCPC) baseParams.cpc_goal = parseFloat(goalCPC).toFixed(2) || null;
    if (maxCPC) baseParams.max_cpc = parseFloat(maxCPC).toFixed(2) || null;
    if (clickTracker) baseParams.click_tracker = clickTracker;
    if (utmParams[0].length)
      baseParams.utm_params = utmParams.filter(params => params[0] && params[1]);

    return accountType === PUBLISHER
      ? {
          ...baseParams,
          publisher_type: pubType.value,
          reserve_cpc: parseFloat(reserveCpc).toFixed(2),
          cogs: Number.isNaN(parseFloat(cogs)) ? cogs : parseFloat(cogs),
          has_jstag: auctionType.value,
          amazon_tracking_id: amazonId || null,
          widget_options: {
            amp: enableAmp,
            domains: validDomains.filter(domain => domain),
            enable_external_script: enableTheTake,
            enable_verbose_logging: false, // TODO
            linkmate: enableLinkmate,
            record_external_clicks: recordExternalClicks,
            jstag_disabled: jstagDisabled,
          },
        }
      : {
          ...baseParams,
          share_goal: parseFloat(shareGoal),
          roi_goal: parseFloat(roiGoal),
          is_feed_importer_enabled: feedEnabled,
          is_active: isActive,
          pricing_model: currentPricingModel.label,
          commission_rate: parseFloat(commissionRate),
          expected_import_frequency: parseInt(expectedFrequency),
          is_pacing_enabled: pacing,
          impression_tracker: impressionTracker || null,
          widget_options: {
            enable_verbose_logging: logging,
            // TODO get context on linkmate from mocks
            linkmate: !!enableLinkmate,
          },
        };
  }

  function getAccountUsers() {
    AccountService.getAccountUsers(accountType, accountId)
      .then(res => {
        const { users } = res.data.data[0];
        setUsers(users);
        const billingUser = users.find(user => user.uid === savedBillingUserId);
        if (billingUser) {
          setSelectedBillingUser({ label: billingUser.email, uid: billingUser.uid });
        }
      })
      .catch(e => showError(`Getting users failed, ${e && e.message}`));
  }

  function getNetworkOrgs() {
    const endpoint = `/api/v0/${accountType}s/${accountId}/network/${otherOrgType}s/`;
    api
      .get(endpoint)
      .then(res => {
        const networkOrgs = res.data.data[0][`${otherOrgType}s`];

        const filteredInNetwork = networkOrgs.filter(org => org.is_in_network);
        const filteredOutOfNetwork = networkOrgs.filter(
          org => !org.is_in_network && (accountType !== PUBLISHER || org.is_active),
        );

        if (editing) {
          setInNetworkOrgs(AccountService.sortByName(filteredInNetwork));
          setOutOfNetworkOrgs(AccountService.sortByName(filteredOutOfNetwork));
        } else {
          const combinedOrgs = AccountService.sortByName(
            filteredInNetwork.concat(filteredOutOfNetwork),
          );
          setInNetworkOrgs(combinedOrgs);
        }
      })
      .catch(e => showError(`Getting network failed, ${e && e.message}`));
  }

  function updateNetwork() {
    const id = otherOrgType === 'publisher' ? 'pub_id' : 'merch_id';
    const inNetworkOrgParams = inNetworkOrgs.map(org => {
      return { [id]: org[id], is_in_network: true };
    });
    const outOfNetworkOrgParams = outOfNetworkOrgs.map(org => {
      return { [id]: org[id], is_in_network: false };
    });
    const params = { [`${otherOrgType}s`]: [...inNetworkOrgParams, ...outOfNetworkOrgParams] };

    return api.put(`/api/v0/${accountType}s/${accountId}/network/`, params);
  }

  function uploadImage() {
    const formData = new FormData();
    formData.append('file', selectedFile);
    return api
      .post(`/api/v0/${accountType}_upload_logo/${accountId}/`, formData)
      .then(res => {
        showError(`Logo edit successful`);
        setAccountLogo(res.data.data[0].url);
      })
      .catch(e => showError(`Logo edit failed. ${e && e.message}`));
  }

  function updateTermsAccepted() {
    return api.put(`/api/v0/publishers/${accountId}/terms_conditions/accept/`);
  }

  function updateBillingUser() {
    const params = {
      pub_id: parseInt(accountId),
      billing_user_uid: parseInt(selectedBillingUser.uid),
    };
    if (account.stripe_account_id) {
      api.put(`/api/v0/billing/connected_accounts/${account.stripe_account_id}/`, params);
    } else {
      api.post('/api/v0/billing/connected_accounts/', params);
    }
  }

  function getSection(name, ref, idx) {
    let form;
    const networkLists = [3, 4].includes(idx) ? (
      <NetworkLists
        inNetworkOrgs={inNetworkOrgs}
        outOfNetworkOrgs={outOfNetworkOrgs}
        setInNetworkOrgs={setInNetworkOrgs}
        setOutOfNetworkOrgs={setOutOfNetworkOrgs}
        otherOrgType={otherOrgType}
      />
    ) : null;

    if (!idx) {
      form = AccountDetails(
        account,
        accountType,
        pubType,
        pubTypes,
        setPubType,
        fieldErrors,
        slug,
        setSlug,
        editableSlug,
        selectedFile,
        fullName,
        setFullName,
        onFileUpload,
        shortName,
        setShortName,
        categories,
        setCategories,
        url,
        setUrl,
        uploadButton,
        handleClickUpload,
        editing,
        pilot,
        setPilot,
        showInDashboard,
        setShowInDashboard,
        isActive,
        setIsActive,
        merchBillingOption,
        setMerchBillingOption,
        campaignInvoicing,
        setCampaignInvoicing,
        flags,
      );
    } else if (idx === 1) {
      const headerValueMap = {
        First: 'first_name',
        Last: 'last_name',
        Email: 'email',
        Role: 'role',
      };

      const headers = Object.keys(headerValueMap).map((header, index) => (
        <div key={index} className={header.toLowerCase()}>
          {header}
        </div>
      ));

      const userItems = users.map((user, userIndex) => (
        <div className="user" key={userIndex}>
          {Object.entries(headerValueMap).map(([key, value], index) => {
            return key === 'Role' ? (
              <div key={index} className={key.toLowerCase()}>
                <BasicDropdown
                  items={roleOptions}
                  selectItem={dropdownIndex => changeRole(roleOptions[dropdownIndex], userIndex)}
                  dropdownLabel={user[value]}
                />
                <span
                  onClick={() => {
                    setModalData(user);
                    setModal('unlink');
                  }}
                >
                  <Icon iconName="x" size={10} />
                </span>
              </div>
            ) : (
              <div
                key={index}
                className={key.toLowerCase()}
                onClick={() => {
                  setModalData(user);
                  setModal('edit');
                }}
              >
                {user[value]}
              </div>
            );
          })}
        </div>
      ));

      const billingUserDropdown =
        accountType === PUBLISHER ? (
          <Flag name="adminBillingFeatures">
            <div className="form-item">
              <label>
                Billing User <span className="star">*</span>
              </label>
              <div className="billing-user">
                <div className={`billing-dropdown ${!selectedBillingUser ? 'none-selected' : ''}`}>
                  <BasicDropdown
                    items={billingUserOptions}
                    selectItem={idx => setSelectedBillingUser(billingUserOptions[idx])}
                    dropdownLabel={selectedBillingUser ? selectedBillingUser.label : 'Select User'}
                  />
                </div>
                <div className="billing-text">
                  This is a mandatory field. The Billing User email needs to be entered to be able
                  to view and update their Stripe account information.
                </div>
              </div>
            </div>
          </Flag>
        ) : null;

      const noUsersTxt = !users.length ? <div className="no-users">No users added.</div> : null;

      const invoiceRecipientField =
        accountType === MERCHANT ? (
          <Flag name="adminBillingFeatures">
            <div className="invoice-area">
              <MandatoryFormItem
                label="Invoice Recipient"
                type="invoice_recipient"
                value={invoiceRecipient}
                setter={setInvoiceRecipient}
                fieldErrors={fieldErrors}
              />
              <div className="sub-text">
                Monthly invoices for marketplace activity will automatically be sent to this email
                address
              </div>
            </div>
          </Flag>
        ) : null;

      form = (
        <div className="form">
          <div className="form-item">
            <div className="users-table">
              <div className="headers">{headers}</div>
              <div className="users">
                {userItems}
                {noUsersTxt}
                <div className="controls">
                  <button onClick={() => setModal('add')}>Create User</button>
                  <button onClick={() => setModal('link')}>Link Existing User</button>
                </div>
              </div>
            </div>
          </div>
          {invoiceRecipientField}
          {billingUserDropdown}
        </div>
      );
    } else if (idx === 2) {
      form =
        accountType === PUBLISHER
          ? PublisherAuctionSetup(
              auctionType,
              auctionTypes,
              setAuctionType,
              validDomains,
              setValidDomains,
              reserveCpc,
              setReserveCpc,
              fieldErrors,
              cogs,
              setCogs,
              amazonId,
              setAmazonId,
              termsAccepted,
              setTermsAccepted,
              enableTheTake,
              setEnableTheTake,
              enableLinkmate,
              setEnableLinkmate,
              enableAmp,
              setEnableAmp,
              recordExternalClicks,
              setRecordExternalClicks,
              defaultAffiliate,
              setDefaultAffiliate,
              affiliateTypes,
              linkmateAffiliate,
              setLinkmateAffiliate,
              notes,
              setNotes,
              skimLinkId,
              setSkimLinkId,
              impactRadiusUrl,
              setImpactRadiusUrl,
              linkShareMemberId,
              setLinkShareMemberId,
              pmgUrl,
              setPmgUrl,
              vigilinkKey,
              setVigilinkKey,
            )
          : FeedConfigurationAndTracking(
              ftpName,
              setFtpName,
              ftpPassword,
              setFtpPassword,
              expectedFrequency,
              setExpectedFrequency,
              impressionTracker,
              setImpressionTracker,
              clickTracker,
              setClickTracker,
              logging,
              setLogging,
              feedEnabled,
              setFeedEnabled,
              utmParams,
              setUtmParams,
              defaultAffiliate,
              setDefaultAffiliate,
              affiliateTypes,
              linkmateAffiliate,
              setLinkmateAffiliate,
              notes,
              setNotes,
              lockFtpName,
              amazonConfig,
              setAmazonConfig,
              impactRadiusUrl,
              setImpactRadiusUrl,
              linkShareMemberId,
              setLinkShareMemberId,
              pmgUrl,
              setPmgUrl,
              skimLinkId,
              setSkimLinkId,
              vigilinkKey,
              setVigilinkKey,
              fieldErrors,
            );
    } else if (idx === 3) {
      form =
        accountType === PUBLISHER
          ? networkLists
          : MerchantAuctionSetup(
              pricingModels,
              currentPricingModel,
              setCurrentPricingModel,
              maxCPC,
              setMaxCPC,
              roiGoal,
              setRoiGoal,
              shareGoal,
              setShareGoal,
              goalCPC,
              setGoalCPC,
              commissionRate,
              setCommissionRate,
              pacing,
              setPacing,
            );
    } else if (idx === 4 && accountType === MERCHANT) {
      form = networkLists;
    }

    const idxIcon = sections.length > 1 ? <span className="idx-icon">{idx + 1}</span> : null;

    return (
      <div
        className={`section ${name
          .toLowerCase()
          .split(' ')
          .join('-')}`}
        key={idx}
        ref={ref}
      >
        <div className="name-icon">
          {idxIcon}
          <h2>{name}</h2>
        </div>
        {form}
      </div>
    );
  }

  function changeRole(role, index) {
    api
      .put(`/api/v0/users/${users[index].uid}/${accountType}s/${match.params.accountId}/`, {
        is_org_admin: role.value,
      })
      .then(res => {
        setUsers(prevState => {
          const newUsers = cloneDeep(prevState);
          newUsers[index].role = role.label;
          return newUsers;
        });
        showError('User role changed');
      })
      .catch(e => showError(`User role change failed. ${e && e.message}`));
  }

  function saveMerchantBilling() {
    const params = {
      merch_id: account.merch_id,
      email: invoiceRecipient,
      invoicing_enabled: merchBillingOption,
      campaign_invoicing_enabled: campaignInvoicing,
    };

    return AccountService.saveMerchantBilling(params, account.stripe_customer_id);
  }

  function handleSubmit() {
    setFieldErrors([]);
    // Using .then instead of async await allows showing errors
    saveSlug()
      .then(res => editAccount())
      .then(res => updateNetwork())
      .then(res => (selectedBillingUser ? updateBillingUser() : null))
      .then(res => (termsAccepted !== account.terms_accepted ? updateTermsAccepted() : null))
      .then(res => (accountType === MERCHANT ? saveMerchantBilling() : null))
      .then(res => {
        showError('Successfully saved');
        history.push(overviewPath);
      })
      .catch(e => {
        if (Array.isArray(e)) {
          showError(e[0]);
          setFieldErrors(e[1]);
        } else {
          showError(e);
        }
      });
  }

  function editAccount() {
    const affiliateConfigArgs = {
      defaultAffiliate: defaultAffiliate.label,
      linkmateAffiliate: linkmateAffiliate.label,
      Amazon: amazonConfig,
      'Impact Radius': impactRadiusUrl,
      Linkshare: linkShareMemberId,
      PMG: pmgUrl,
      Skimlinks: skimLinkId,
      Viglink: vigilinkKey,
    };

    return AccountService.editAccount(getParams(), accountType, accountId, affiliateConfigArgs);
  }

  function showError(e) {
    const error = e ? (e.message ? e.message : e) : '';
    setSnackbarTxt(error);
  }

  function handleClickUpload() {
    // This cannot be done inline to work after navigating to this page
    uploadButton.current.click();
  }

  function onFileUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setFile(file);
  }

  function getBillingUserOptions() {
    const userOptions = cloneDeep(users).map(user => ({ ...user, label: user.email }));
    setBillingUserOptions(userOptions);
  }

  const unlinkModal =
    modal === 'unlink' ? (
      <UnlinkModal
        userData={modalData}
        accountType={accountType}
        accountId={accountId}
        setUsers={setUsers}
        setShowModal={setModal}
        company={fullName}
      />
    ) : null;

  const userModal = ['edit', 'link', 'add'].includes(modal) ? (
    <UserModal
      shortName={shortName}
      setUsers={setUsers}
      setShowModal={setModal}
      modal={modal}
      accountId={accountId}
      showError={showError}
      userData={modalData}
      accountType={accountType}
    />
  ) : null;

  const sectionNames =
    sections.length > 1 ? (
      <div className="section-names">
        {sections.map(({ name, ref }, idx) => (
          <div
            className="name-icon"
            key={idx}
            onClick={() => ref.current.scrollIntoView({ behavior: 'smooth' })}
          >
            <span className="idx-icon">{idx + 1}</span>
            <h2>{name}</h2>
          </div>
        ))}
      </div>
    ) : null;

  const snackBar = snackbarTxt ? (
    <SnackBar message={snackbarTxt} setSnackbarTxt={setSnackbarTxt} />
  ) : null;

  return (
    <div className="manage-account-container">
      {snackBar}
      {unlinkModal}
      {userModal}
      <div className="navigation">
        <Link className="nav-link" to={overviewPath}>
          Accounts
        </Link>
        <img src={BreadCrumbArrow} alt="arrow" className="breadcrumb-arrow" />
        <span>
          {editing ? 'Edit' : 'Create'} {accountType} Account
        </span>
      </div>
      <h1>
        {editing ? 'Edit' : 'Create'} {accountType} Account
      </h1>
      {sectionNames}
      <div className="sections">
        {sections.map(({ name, ref }, index) => getSection(name, ref, index))}
      </div>
      <div className="controls">
        <Link to="/v2/admin/accounts/overview/">
          <button className="cancel">Cancel</button>
        </Link>
        <button className="create" onClick={handleSubmit}>
          Save {editing ? 'Changes' : 'Account'}
        </button>
      </div>
    </div>
  );
};

export default withLDConsumer()(ManageAccount);
